import { useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'sonner';

import {
  usePatchLoanFocusProductMutation,
  usePatchLoanPropertyMutation,
} from '@/apis/extended/loanApplicationApi';
import { useGetProductQuery } from '@/apis/extended/productApi';
import { useEstimation } from '@/hooks/useEstimation';
import { useProperties } from '@/hooks/useProperties';
import { resolveProductId } from '@/utils/loanApplicationHub';
import {
  formatCurrencyField,
  isInvestorPropertyOperatingContext,
  operatingFiguresFromSources,
  resolveSubjectAddress,
} from '@/utils/loanApplicationProperty';
import type {
  LoanApplication,
  LoanPropertySnapshot,
} from '@/types/LoanApplicationType';
import type { Product } from '@/types/ProductType';
import type { PropertyView } from '@/types/PropertyView';
import { COMMERCIAL_PROPERTY_TYPES } from './constants';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { MoneyField } from './MoneyField';
import { useLoanSectionForm } from './useLoanSectionForm';

const currencyStart = {
  startAdornment: <InputAdornment position="start">$</InputAdornment>,
};

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
  /** Deal-room embedded shell — property comes from the active listing */
  propertyContext?: PropertyView | null;
  variant?: 'propertyScoped' | 'standalone';
};

const emptyProperty: LoanPropertySnapshot = {
  subjectPropertyAddress: '',
  propertyCounty: '',
  apartmentUnit: '',
  propertyType: '',
  numberOfUnits: null,
  proposedMonthlyTaxes: null,
  proposedMonthlyInsurance: null,
  proposedMonthlyRent: null,
  proposedMonthlyHoaDues: null,
  yearAcquired: null,
  amountExistingLiens: null,
  yearBuilt: null,
  titleVesting: '',
  legalDescription: '',
};

export function PropertySection({
  application,
  readOnly,
  propertyContext,
  variant = 'standalone',
}: Props) {
  const propertyScoped = variant === 'propertyScoped';
  const isRefinance = application.flowProfile === 'refinance';
  const showInvestorOperating = isInvestorPropertyOperatingContext(application);

  const focusProductId = resolveProductId(
    propertyContext?.productId ??
      propertyContext?.id ??
      application.focusProductId ??
      application.sourceProductId
  );
  const { data: focusProduct } = useGetProductQuery(focusProductId, {
    skip: !propertyScoped || !focusProductId,
  });

  const { estimation } = useEstimation(
    (focusProduct ?? {
      _id: focusProductId || 'pending',
      price: 0,
    }) as Product,
    undefined,
    undefined
  );

  const resolvedAddress = useMemo(
    () => resolveSubjectAddress(propertyContext, focusProduct),
    [propertyContext, focusProduct]
  );

  const computedOperating = useMemo(
    () =>
      operatingFiguresFromSources(
        focusProduct,
        focusProduct ? estimation : null
      ),
    [focusProduct, estimation]
  );

  const [patch, { isLoading }] = usePatchLoanPropertyMutation();
  const [patchFocus, { isLoading: linking }] =
    usePatchLoanFocusProductMutation();
  const syncRef = useRef<string>('');
  const { allProperties } = useProperties();
  const isLinked = Boolean(focusProductId);
  // Homes this file could be for: owned ones on a refinance, ones being
  // bought on a purchase.
  const linkCandidates = useMemo(
    () =>
      allProperties.filter(
        (p) => p.pipelineMode !== 'seller' && p.isRefinance === isRefinance
      ),
    [allProperties, isRefinance]
  );
  const linkProperty = async (productId: string) => {
    try {
      await patchFocus({ id: application._id, focusProductId: productId })
        .unwrap()
        .then(() => toast.success('Property linked'));
    } catch {
      toast.error('Could not link that property');
    }
  };

  const initialProperty = useMemo(
    () => ({ ...emptyProperty, ...(application.property || {}) }),
    [application._id, application.property, application.updatedAt]
  );

  const {
    draft: local,
    setDraft: setLocal,
    isDirty,
    markSaved,
    resetDraft,
    mergeDraftFromListing,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'property',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initialProperty,
    readOnly,
  });

  useEffect(() => {
    if (!propertyScoped) return;
    const { line, county } = resolvedAddress;

    mergeDraftFromListing((s) => ({
      ...s,
      subjectPropertyAddress: line || s.subjectPropertyAddress,
      propertyCounty: county || s.propertyCounty,
      proposedMonthlyTaxes:
        computedOperating.proposedMonthlyTaxes ?? s.proposedMonthlyTaxes,
      proposedMonthlyInsurance:
        computedOperating.proposedMonthlyInsurance ??
        s.proposedMonthlyInsurance,
      ...(showInvestorOperating
        ? {
            proposedMonthlyRent:
              computedOperating.proposedMonthlyRent ?? s.proposedMonthlyRent,
            proposedMonthlyHoaDues:
              computedOperating.proposedMonthlyHoaDues ??
              s.proposedMonthlyHoaDues,
          }
        : {}),
    }));
  }, [
    propertyScoped,
    resolvedAddress,
    computedOperating,
    showInvestorOperating,
    mergeDraftFromListing,
  ]);

  useEffect(() => {
    if (!propertyScoped || readOnly) return;
    const { line, county } = resolvedAddress;
    const patchBody: Partial<LoanPropertySnapshot> = {};
    const saved = application.property ?? {};
    if (line && line !== (saved.subjectPropertyAddress ?? '')) {
      patchBody.subjectPropertyAddress = line;
    }
    if (county && county !== (saved.propertyCounty ?? '')) {
      patchBody.propertyCounty = county;
    }
    if (
      computedOperating.proposedMonthlyTaxes != null &&
      computedOperating.proposedMonthlyTaxes !== saved.proposedMonthlyTaxes
    ) {
      patchBody.proposedMonthlyTaxes = computedOperating.proposedMonthlyTaxes;
    }
    if (
      computedOperating.proposedMonthlyInsurance != null &&
      computedOperating.proposedMonthlyInsurance !==
        saved.proposedMonthlyInsurance
    ) {
      patchBody.proposedMonthlyInsurance =
        computedOperating.proposedMonthlyInsurance;
    }
    if (showInvestorOperating) {
      if (
        computedOperating.proposedMonthlyRent != null &&
        computedOperating.proposedMonthlyRent !== saved.proposedMonthlyRent
      ) {
        patchBody.proposedMonthlyRent = computedOperating.proposedMonthlyRent;
      }
      if (
        computedOperating.proposedMonthlyHoaDues != null &&
        computedOperating.proposedMonthlyHoaDues !==
          saved.proposedMonthlyHoaDues
      ) {
        patchBody.proposedMonthlyHoaDues =
          computedOperating.proposedMonthlyHoaDues;
      }
    }
    if (!Object.keys(patchBody).length) return;

    const key = JSON.stringify(patchBody);
    if (syncRef.current === key) return;
    syncRef.current = key;

    void patch({ id: application._id, property: patchBody }).catch(() => {
      syncRef.current = '';
    });
  }, [
    propertyScoped,
    readOnly,
    application._id,
    application.property,
    resolvedAddress,
    computedOperating,
    showInvestorOperating,
    patch,
  ]);

  const saveSection = async () => {
    if (formReadOnly) return;
    const payload: LoanPropertySnapshot = isRefinance
      ? local
      : { ...local, yearAcquired: null, amountExistingLiens: null };
    try {
      await patch({
        id: application._id,
        property: payload,
      }).unwrap();
      markSaved(payload);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly;

  const footer = (
    <LoanSectionFormFooter
      readOnly={formReadOnly}
      isDirty={isDirty}
      isSaving={isLoading}
      saveLabel="Save property"
      onSave={() => void saveSection()}
      onCancel={resetDraft}
    />
  );
  const displayAddress =
    resolvedAddress.line || local.subjectPropertyAddress || '';

  if (!propertyScoped) {
    return (
      <>
        <StandalonePropertyFields
          local={local}
          setLocal={setLocal}
          disabled={disabled}
        />
        {footer}
      </>
    );
  }

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isRefinance
          ? 'The home you are refinancing. Address, taxes and insurance come from your property.'
          : 'The home you are buying. Address, taxes and insurance come from your property.'}
      </Typography>
      {!isLinked && !formReadOnly && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {isRefinance
              ? 'Which home are you refinancing?'
              : 'Which home are you buying?'}
          </Typography>
          {linkCandidates.length > 0 ? (
            <TextField
              select
              fullWidth
              size="small"
              label="Choose a saved property"
              value=""
              disabled={linking}
              onChange={(e) => void linkProperty(e.target.value)}
              sx={{ mb: 1.5 }}
            >
              {linkCandidates.map((p) => (
                <MenuItem key={p.productId} value={p.productId}>
                  {p.address}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {linkCandidates.length > 0
              ? 'Or add it to your properties — we will fill in its taxes, insurance and value for you.'
              : isRefinance
              ? 'Add your home to your properties and we will fill in its value, taxes, insurance and current mortgage here.'
              : "Add the home to your properties and we will fill in its price, taxes and insurance here. Haven't picked one yet? Type the address below if you know it."}
          </Typography>
          <Button
            component={RouterLink}
            to={`/properties?add=1${
              isRefinance ? '&isRefinance=1' : ''
            }&linkApplication=${application._id}`}
            size="small"
            variant="outlined"
            startIcon={<i className="fa-light fa-plus" />}
          >
            Add a property
          </Button>
        </Box>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Property address"
            value={
              isLinked ? displayAddress : local.subjectPropertyAddress ?? ''
            }
            disabled={isLinked || disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                subjectPropertyAddress: e.target.value,
              }))
            }
            placeholder="Street, city, state ZIP"
            helperText={
              isLinked
                ? 'From your saved property — edit the property if the address changes'
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Property county (optional)"
            value={local.propertyCounty ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, propertyCounty: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Apartment / unit #"
            value={local.apartmentUnit ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, apartmentUnit: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            select
            label="Property type"
            value={local.propertyType ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, propertyType: e.target.value }))
            }
          >
            <MenuItem value="">Select…</MenuItem>
            {COMMERCIAL_PROPERTY_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Number of units"
            type="number"
            value={local.numberOfUnits ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                numberOfUnits:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {computedOperating.proposedMonthlyTaxes != null ? (
            <TextField
              fullWidth
              size="small"
              label="Monthly property taxes"
              value={formatCurrencyField(
                computedOperating.proposedMonthlyTaxes
              )}
              disabled
              InputProps={currencyStart}
              helperText="From your loan estimate for this property"
            />
          ) : (
            <MoneyField
              size="small"
              label="Monthly property taxes"
              value={local.proposedMonthlyTaxes}
              disabled={disabled}
              onChange={(v) =>
                setLocal((s) => ({ ...s, proposedMonthlyTaxes: v }))
              }
              helperText="Annual tax bill ÷ 12"
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {computedOperating.proposedMonthlyInsurance != null ? (
            <TextField
              fullWidth
              size="small"
              label="Monthly homeowners insurance"
              value={formatCurrencyField(
                computedOperating.proposedMonthlyInsurance
              )}
              disabled
              InputProps={currencyStart}
              helperText="From your loan estimate for this property"
            />
          ) : (
            <MoneyField
              size="small"
              label="Monthly homeowners insurance"
              value={local.proposedMonthlyInsurance}
              disabled={disabled}
              onChange={(v) =>
                setLocal((s) => ({ ...s, proposedMonthlyInsurance: v }))
              }
              helperText="Annual premium ÷ 12"
            />
          )}
        </Grid>
        {showInvestorOperating && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Proposed monthly rent (market)"
                value={formatCurrencyField(
                  computedOperating.proposedMonthlyRent ??
                    local.proposedMonthlyRent
                )}
                disabled
                InputProps={currencyStart}
                helperText="From your DSCR / investor loan estimate"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Proposed monthly HOA dues"
                value={formatCurrencyField(
                  computedOperating.proposedMonthlyHoaDues ??
                    local.proposedMonthlyHoaDues
                )}
                disabled
                InputProps={currencyStart}
                helperText="From your loan estimate for this property"
              />
            </Grid>
          </>
        )}
        {isRefinance && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Year acquired"
                type="number"
                value={local.yearAcquired ?? ''}
                disabled={disabled}
                onChange={(e) =>
                  setLocal((s) => ({
                    ...s,
                    yearAcquired:
                      e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
                helperText="When you originally purchased or took title to this home"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MoneyField
                size="small"
                label="Mortgage balance (all liens)"
                value={local.amountExistingLiens}
                disabled={disabled}
                onChange={(v) =>
                  setLocal((s) => ({ ...s, amountExistingLiens: v }))
                }
                helperText="Same number as your current loan balance — change it in either place and the other follows, along with Debts"
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Year built"
            type="number"
            value={local.yearBuilt ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                yearBuilt:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Title vesting"
            value={local.titleVesting ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, titleVesting: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Legal description (optional)"
            value={local.legalDescription ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, legalDescription: e.target.value }))
            }
          />
        </Grid>
      </Grid>
      {footer}
    </div>
  );
}

function StandalonePropertyFields({
  local,
  setLocal,
  disabled,
}: {
  local: LoanPropertySnapshot;
  setLocal: React.Dispatch<React.SetStateAction<LoanPropertySnapshot>>;
  disabled: boolean;
}) {
  return (
    <div>
      <Alert severity="info" sx={{ mb: 2 }}>
        Property address, county, and monthly operating figures are managed per
        property in your deal room (Stages → Properties). Use the
        subject-property chips above when this file covers multiple homes.
      </Alert>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        File-level property details that apply across the application.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Year built"
            type="number"
            value={local.yearBuilt ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                yearBuilt:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Title vesting"
            value={local.titleVesting ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, titleVesting: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Legal description (optional)"
            value={local.legalDescription ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, legalDescription: e.target.value }))
            }
          />
        </Grid>
      </Grid>
    </div>
  );
}
